'use client'

/**
 * Primitives visuelles du LECTEUR (design BLC Médias 2026-09, accent violet).
 * Partagées par la PlayerBar, le lecteur plein écran, le mini-lecteur flottant…
 * Purement visuelles : aucune logique de lecture ici.
 *
 * Thème « baffle » (bleu) : passer `tone="baffle"` — le lecteur de la soirée
 * garde volontairement son dégradé bleu (logo baffle).
 */
import type { CSSProperties, ReactNode, MouseEvent } from 'react'
import { Play, Pause, Loader2 } from 'lucide-react'
import { cx } from '@/components/ui/kit'

export type LecteurTone = 'violet' | 'baffle'

/** Dégradés de référence. */
export const GRAD = {
  play: 'linear-gradient(135deg, rgb(var(--a2)) 0%, rgb(var(--a1)) 100%)',
  playBaffle: 'linear-gradient(135deg, #22D3EE 0%, #2563EB 100%)',
  bar: ['rgb(var(--a1))', 'rgb(var(--a3))'] as const,
  barBaffle: ['#22D3EE', '#2563EB'] as const,
}

/** Fond d'un curseur (range) : partie remplie en dégradé + piste discrète. */
export function rangeBg(pct: number, tone: LecteurTone = 'violet', track = 'rgba(255,255,255,0.12)') {
  const [a, b] = tone === 'baffle' ? GRAD.barBaffle : GRAD.bar
  const p = Math.max(0, Math.min(100, pct || 0))
  return `linear-gradient(90deg, ${a} 0%, ${b} ${p}%, ${track} ${p}%, ${track} 100%)`
}

/** Curseur (progression / volume) au style violet. */
export function RangeSlider({
  value, max, onChange, pct, tone = 'violet', className = '', height = 5, disabled, ariaLabel, title, style, ...rest
}: {
  value: number; max: number; onChange: (v: number) => void; pct?: number; tone?: LecteurTone
  className?: string; height?: number; disabled?: boolean; ariaLabel?: string; title?: string; style?: CSSProperties
  onPointerDown?: (e: React.PointerEvent<HTMLInputElement>) => void; onClick?: (e: React.MouseEvent<HTMLInputElement>) => void
}) {
  const p = pct ?? (max > 0 ? (value / max) * 100 : 0)
  return (
    <input
      type="range" min={0} max={Math.max(1, max)} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={disabled} aria-label={ariaLabel} title={title}
      className={cx('blc-lecteur-range w-full disabled:opacity-40', className)}
      style={{ height, borderRadius: 999, appearance: 'none', background: rangeBg(p, tone), ...style }}
      {...rest}
    />
  )
}

/** GROS bouton lecture rond violet lumineux (halo). */
export function BigPlayButton({
  playing, loading, onClick, size = 56, tone = 'violet', disabled, className = '', title,
}: {
  playing: boolean; loading?: boolean; onClick?: (e: MouseEvent) => void; size?: number; tone?: LecteurTone
  disabled?: boolean; className?: string; title?: string
}) {
  const icon = Math.round(size * 0.4)
  const glow = tone === 'baffle' ? 'rgba(37,99,235,0.55)' : 'rgb(var(--a2)/0.55)'
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      title={title ?? (playing ? 'Pause' : 'Lecture')} aria-label={title ?? (playing ? 'Pause' : 'Lecture')}
      className={cx('lglass-accent shrink-0 rounded-full grid place-items-center text-white transition hover:brightness-110 active:scale-95 disabled:opacity-50 disabled:pointer-events-none', className)}
      style={{
        width: size, height: size,
        background: tone === 'baffle' ? GRAD.playBaffle : GRAD.play,
        boxShadow: `0 8px 24px ${glow}, 0 0 ${Math.round(size * 0.55)}px ${glow}, inset 0 1px 0 rgba(255,255,255,0.22)`,
      }}>
      {loading
        ? <Loader2 size={icon} className="animate-spin" />
        : playing ? <Pause size={icon} className="fill-white" /> : <Play size={icon} className="fill-white" style={{ marginLeft: Math.round(size * 0.05) }} />}
    </button>
  )
}

/** Bouton icône transparent du lecteur (aléatoire, répéter, file…). */
export function CtrlIcon({
  children, onClick, active, title, className = '', disabled, badge, tone = 'violet', ...rest
}: {
  children: ReactNode; onClick?: (e: MouseEvent<HTMLButtonElement>) => void; active?: boolean; title?: string
  className?: string; disabled?: boolean; badge?: ReactNode; tone?: LecteurTone; [k: string]: any
}) {
  const on = tone === 'baffle' ? 'text-[#60A5FA]' : 'text-a4'
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title} aria-pressed={active}
      className={cx('lglass relative shrink-0 grid place-items-center rounded-full transition active:scale-90 disabled:opacity-40 disabled:pointer-events-none',
        active ? on : 'text-white/65 hover:text-white', className)}
      {...rest}>
      {children}
      {active && !badge && <span className={cx('absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full', tone === 'baffle' ? 'bg-[#60A5FA]' : 'bg-a4')} />}
      {badge}
    </button>
  )
}

/** Bouton rond sombre (transport secondaire dans le lecteur plein écran). */
export function RoundCtrl({
  children, onClick, active, title, className = '', disabled, size = 48, tone = 'violet',
}: {
  children: ReactNode; onClick?: () => void; active?: boolean; title?: string; className?: string
  disabled?: boolean; size?: number; tone?: LecteurTone
}) {
  const on = tone === 'baffle'
    ? 'text-[#93C5FD] border-[#3B82F6]/50 bg-[#2563EB]/15'
    : 'text-a5 border-a2/55 bg-a2/15'
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title} aria-pressed={active}
      style={{ width: size, height: size }}
      className={cx('lglass relative shrink-0 rounded-full grid place-items-center border transition active:scale-90 disabled:opacity-40',
        active ? on : 'text-white border-white/[0.09] bg-white/[0.05] hover:bg-white/[0.1] hover:border-white/20', className)}>
      {children}
    </button>
  )
}

/** Pastille « 1 » (répéter un seul titre). */
export function RepeatOneBadge({ tone = 'violet' }: { tone?: LecteurTone }) {
  return (
    <span className={cx('absolute -top-1 -right-1 text-[9px] font-bold text-white rounded-full w-4 h-4 grid place-items-center',
      tone === 'baffle' ? 'bg-[#2563EB]' : 'bg-a2')}>1</span>
  )
}

/** Petites barres d'égaliseur animées (piste en cours). */
export function EqBars({ className = '', color = 'bg-a4' }: { className?: string; color?: string }) {
  return (
    <span className={cx('flex items-end gap-0.5 h-3.5', className)}>
      {[0, 0.15, 0.3].map((d, k) => <span key={k} className={cx('w-[3px] rounded-sm animate-equalizer', color)} style={{ height: '100%', animationDelay: `${d}s` }} />)}
    </span>
  )
}
